import { call, race, select, take, takeEvery } from 'redux-saga/effects';
import {
  GlobalAction,
  GlobalActions,
  GlobalPut,
  Zaehlstueck,
  ZAEHLSTUECKEREQUEST,
} from '@ps20-frontend/shop-integration-redux';
import {
  SalesProduktZaehlStueckLogik,
  SpartenType,
  ZaehlStueckLogikProdukt,
  ZaehlStueckLogikRequestModel,
  ZaehlStueckLogikResponseItem,
  ZaehlStueckLogikResponseModel,
} from '@ps20/bff-models';
import { App, convertToBffLaufzeit, getSpartenTypeByApp } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { UnfallZaehlstueck } from '../reducers/unfallZaehlstueckReducer';
import { selectUnfallProdukt } from '../selectors/unfallProdukt.selector';
import {
  angebotIsTarifiert,
  getTarifierteAngebote,
  selectWarenkorbAngebote,
} from '../selectors/warenkorb.selector';

const IGNORED_APPS = new Set([App.JHP, App.GEGE]);

export function* watchZaehlstueckLogik() {
  yield takeEvery(GlobalActions.UPDATE_ZAEHLSTUECKE, safeSaga(getZaehlstuecke, App.SHOP_DASHBOARD));
  yield takeEvery(
    GlobalActions.SAVED_STATES_LOADED,
    safeSaga(recalculateZaehlstueckeAfterLoad, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    ZAEHLSTUECKEREQUEST.ZAEHLSTUECKE_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_DASHBOARD),
  );
}

export function* recalculateZaehlstueckeAfterLoad() {
  const tarifierteAngebote: Array<WarenkorbAngebot> = yield select(getTarifierteAngebote);
  if (tarifierteAngebote.length > 0) {
    yield call(getZaehlstuecke);
    const { success } = yield race({
      success: take(GlobalActions.ZAEHLSTUECKE_RECEIVED),
      failed: take(ZAEHLSTUECKEREQUEST.ZAEHLSTUECKE_REQUEST_FAILED),
    });
    if (success) {
      const globalPut = GlobalPut.getInstance();
      yield call(globalPut.put, new GlobalActions().triggerTarifierung());
    }
  }
}

export function* getZaehlstuecke() {
  const url = '/privatschutz/api/zaehlstuecke';

  const produkteInWarenkorb: Array<WarenkorbAngebot> = (
    (yield select(getTarifierteAngebote)) as WarenkorbAngebot[]
  ).filter(produkt => !IGNORED_APPS.has(produkt.appId));

  const unfallProdukt: UnfallZaehlstueck = yield select(selectUnfallProdukt);

  const zaehlStueckLogikLaufzeitList: Array<ZaehlStueckLogikProdukt> = produkteInWarenkorb.map(
    produkt => {
      let sparteType = getSpartenTypeByApp(produkt.appId);

      if (produkt.appId === App.TKV && produkt.beibehalten) {
        sparteType = SpartenType.TIERKRANKEN;
      }

      const zaehlstueckLogikProdukt: ZaehlStueckLogikProdukt = {
        sparteType,
        laufzeit: convertToBffLaufzeit(produkt.laufzeit),
        neugeschaeft: !produkt.vorvertragsNummern,
        salesProdukt: (SalesProduktZaehlStueckLogik as Record<string, SalesProduktZaehlStueckLogik>)[produkt.kurztext],
      };

      if (produkt.angebotsId) {
        zaehlstueckLogikProdukt.angebotsId = produkt.angebotsId;
      }

      return zaehlstueckLogikProdukt;
    },
  );

  const body: ZaehlStueckLogikRequestModel = {
    zaehlStueckLogikLaufzeitList,
    unfallZaehlstuecke: unfallProdukt.isSelected ? unfallProdukt.zaehlstuecke : 0,
  };

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      appId: App.SHOP_DASHBOARD,
      body,
      url,
      successEvent: ZAEHLSTUECKEREQUEST.ZAEHLSTUECKE_REQUEST_SUCCESSFUL,
      errorEvent: ZAEHLSTUECKEREQUEST.ZAEHLSTUECKE_REQUEST_FAILED,
    }),
  );
}

export function* successCallback(action: GlobalAction<ZaehlStueckLogikResponseModel>) {
  const actionPayload: ZaehlStueckLogikResponseModel = action.payload;
  const angeboteInWarenkorb: Array<WarenkorbAngebot> = yield select(selectWarenkorbAngebote);
  const tarifierteAngebote = angeboteInWarenkorb
    .filter(angebotIsTarifiert)
    .filter(produkt => produkt.appId !== App.JHP)
    .filter(produkt => produkt.appId !== App.GEGE);
  const mappedZaehlstuecke: Array<Zaehlstueck> = tarifierteAngebote.map(produkt => {
    let expectedSparteType = getSpartenTypeByApp(produkt.appId);

    if (produkt.appId === App.TKV && produkt.beibehalten) {
      expectedSparteType = SpartenType.TIERKRANKEN;
    }

    const zaehlStueckLogikResponseItem: ZaehlStueckLogikResponseItem =
      actionPayload.zaehlStueckLogikResponseItems.find(
        item =>
          item.spartenType === expectedSparteType &&
          item.angebotsId === (produkt.angebotsId || null),
      );

    return {
      angebotsId: produkt.angebotsId,
      appId: produkt.appId,
      kurztext: produkt.kurztext,
      rabattstufe: zaehlStueckLogikResponseItem.zaehlstuecke,
      isZaehlstueck: zaehlStueckLogikResponseItem.zaehlStueck,
    };
  });

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    new GlobalActions().zaehlstueckeReceived({
      effektiveZaehlstuecke: actionPayload.effektiveZaehlstuecke,
      zaehlstuecke: mappedZaehlstuecke,
    }),
  );
}
